import * as configcat from "@configcat/sdk/browser";
import { PollingMode, type User } from "@configcat/sdk/browser";
import { getBrowserMockConfigCatOverrides } from "../mocks/browserMockRuntime";
import { CONFIG_CAT_KEY } from "./environment.ts";
import { getConfigCatLogLevelFromStorage } from "./utils/storage/loggerStorageUtils";

// All the keys we use in ConfigCat
export enum ConfigCatKeys {
   BARCODESCANNER_AVAILABLE = "barcodeScannerAvailable",
   CLAIMS_AVAILABLE = "claimsAvailable",
   NOTIFICATIONS_AVAILABLE = "notificationsAvailable",
   NEW_PROMOTION_NOTIFICATIONS_AVAILABLE = "newPromotionNotificationsAvailable",
   RECOMMENDATION_TYPE = "recommendationType",
   KEYFIGURES_AVAILABLE = "keyFiguresAvailable",
   CONTRACTUAL_RECOMMENDATIONS = "contractualRecommendations",
   VOUCHERIFY_AVAILABLE = "voucherifyAvailable",
   LOGIN_AVAILABLE = "loginAvailable",
   LOGIN_UNAVAILABLE_TEXT = "loginUnavailableText",
   PRODUCT_NAVIGATION_AVAILABLE = "productNavigationAvailable",
   USE_DYNAMICS_FOR_NOTIFICATION_PROFILES = "useDynamicsForNotificationProfiles",
   PRIORITIZE_AGREEMENT_ASSORTMENT = "prioritizeAgreementAssortment",
   SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES = "searchResultsSortedByCustomerGroupSales",
   NEW_ORDER_FLOW_ENABLED = "newOrderFlowEnabled",
   LIVE_DELIVERY_STATUS_ENABLED = "liveDeliveryStatusEnabled",
   FILTER_DISPLAY_THRESHOLD = "filterdisplaythreshold",
   LABELS_IN_QUICK_SEARCH = "labelsInQuickSearch",
   SHOW_EDIT_DELIVERY_ADDRESS_BUTTON = "showEditDeliveryAddressButton",
   CONTENTFUL_CACHING_ENABLED = "contentfulCachingEnabled"
}

// Define a mapping type that associates keys with return types to allow TypeScript to
// infer the correct return type based on the key.
type ConfigCatKeyReturnType = {
   [ConfigCatKeys.BARCODESCANNER_AVAILABLE]: boolean;
   [ConfigCatKeys.CLAIMS_AVAILABLE]: boolean;
   [ConfigCatKeys.NOTIFICATIONS_AVAILABLE]: boolean;
   [ConfigCatKeys.NEW_PROMOTION_NOTIFICATIONS_AVAILABLE]: boolean;
   [ConfigCatKeys.KEYFIGURES_AVAILABLE]: boolean;
   [ConfigCatKeys.RECOMMENDATION_TYPE]: "AI1" | "AI2" | "AI3" | "OFF";
   [ConfigCatKeys.CONTRACTUAL_RECOMMENDATIONS]: boolean;
   [ConfigCatKeys.VOUCHERIFY_AVAILABLE]: boolean;
   [ConfigCatKeys.LOGIN_AVAILABLE]: boolean;
   [ConfigCatKeys.LOGIN_UNAVAILABLE_TEXT]: string;
   [ConfigCatKeys.PRODUCT_NAVIGATION_AVAILABLE]: boolean;
   [ConfigCatKeys.USE_DYNAMICS_FOR_NOTIFICATION_PROFILES]: boolean;
   [ConfigCatKeys.PRIORITIZE_AGREEMENT_ASSORTMENT]: boolean;
   [ConfigCatKeys.SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES]: boolean;
   [ConfigCatKeys.NEW_ORDER_FLOW_ENABLED]: boolean;
   [ConfigCatKeys.LIVE_DELIVERY_STATUS_ENABLED]: boolean;
   [ConfigCatKeys.FILTER_DISPLAY_THRESHOLD]: number;
   [ConfigCatKeys.LABELS_IN_QUICK_SEARCH]: boolean;
   [ConfigCatKeys.SHOW_EDIT_DELIVERY_ADDRESS_BUTTON]: boolean;
   [ConfigCatKeys.CONTENTFUL_CACHING_ENABLED]: boolean;
};

// Define a mapping object with default values for each key now that we can no longer default to false
const defaultValueReturnValues = {
   [ConfigCatKeys.BARCODESCANNER_AVAILABLE]: false,
   [ConfigCatKeys.CLAIMS_AVAILABLE]: false,
   [ConfigCatKeys.NOTIFICATIONS_AVAILABLE]: false,
   [ConfigCatKeys.NEW_PROMOTION_NOTIFICATIONS_AVAILABLE]: false,
   [ConfigCatKeys.KEYFIGURES_AVAILABLE]: false,
   [ConfigCatKeys.RECOMMENDATION_TYPE]: "AI2",
   [ConfigCatKeys.CONTRACTUAL_RECOMMENDATIONS]: false,
   [ConfigCatKeys.VOUCHERIFY_AVAILABLE]: false,
   [ConfigCatKeys.LOGIN_AVAILABLE]: true,
   [ConfigCatKeys.LOGIN_UNAVAILABLE_TEXT]: "Innlogging er ikke tilgjengelig.",
   [ConfigCatKeys.PRODUCT_NAVIGATION_AVAILABLE]: true,
   [ConfigCatKeys.USE_DYNAMICS_FOR_NOTIFICATION_PROFILES]: false,
   [ConfigCatKeys.PRIORITIZE_AGREEMENT_ASSORTMENT]: false,
   [ConfigCatKeys.SEARCH_RESULTS_SORTED_BY_CUSTOMER_GROUP_SALES]: false,
   [ConfigCatKeys.NEW_ORDER_FLOW_ENABLED]: false,
   [ConfigCatKeys.LIVE_DELIVERY_STATUS_ENABLED]: false,
   [ConfigCatKeys.FILTER_DISPLAY_THRESHOLD]: 8,
   [ConfigCatKeys.LABELS_IN_QUICK_SEARCH]: false,
   [ConfigCatKeys.SHOW_EDIT_DELIVERY_ADDRESS_BUTTON]: false,
   [ConfigCatKeys.CONTENTFUL_CACHING_ENABLED]: false
};

/**
 * Set up ConfigCat client so we can do true feature toggles
 */

const logLevelFromStorage = Number.parseInt(getConfigCatLogLevelFromStorage());

const browserMockFlagOverrides = getBrowserMockConfigCatOverrides();

const configCatClient = configcat.getClient(CONFIG_CAT_KEY || "MISSING-API-KEY", PollingMode.AutoPoll, {
   pollIntervalSeconds: 60 * 20,
   logger: configcat.createConsoleLogger(logLevelFromStorage),
   flagOverrides: browserMockFlagOverrides
      ? configcat.createFlagOverridesFromMap(browserMockFlagOverrides, configcat.OverrideBehaviour.LocalOnly)
      : undefined
});

export function getConfigCatValue<K extends ConfigCatKeys>(key: K, userData?: User): Promise<ConfigCatKeyReturnType[K]>;

/** Fetches a value from ConfigCat, with a fallback value to false if it's not available. */
export async function getConfigCatValue<K extends ConfigCatKeys>(key: K, userData?: User): Promise<ConfigCatKeyReturnType[K]> {
   const value = await configCatClient.getValueAsync(key, defaultValueReturnValues[key], userData).catch((_e) => {
      return defaultValueReturnValues[key];
   });
   if (key === ConfigCatKeys.RECOMMENDATION_TYPE || key === ConfigCatKeys.LOGIN_UNAVAILABLE_TEXT) {
      return value as ConfigCatKeyReturnType[K];
   }
   if (key === ConfigCatKeys.FILTER_DISPLAY_THRESHOLD) {
      const num = Number(value);
      return (Number.isNaN(num) ? defaultValueReturnValues[key] : num) as ConfigCatKeyReturnType[K];
   }
   return Boolean(value) as ConfigCatKeyReturnType[K];
}
